/* eslint-disable @atlaskit/design-system/prefer-primitives */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { N0, N20, N60A, R300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { NowLocation } from '../../../utils/constants/schedule';
import { computeBorderRadius } from '../../../utils/shift';
import { ROTATION_HEIGHT } from '../timeline/constants';

import {
	borderColor,
	CONTENT_PADDING,
	DELETED_IMAGE,
	ICON_MARGIN,
	iconColor,
	Layout,
	OVERRIDE_BORDER,
	OVERRIDE_IMAGE,
	PROFILE_SIZE,
	SHIFT_BORDER,
	SHIFT_RADIUS,
	TIME_FONT_SIZE,
	TIME_LINE_HEIGHT,
} from './constants';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Outer = styled.div<{
	width: number;
	left: number;
	size: 'small' | 'medium' | 'large';
}>(
	{
		position: 'absolute',
		zIndex: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ width, left, size }) =>
		`
    left: ${left}px;
    width: ${width}px;
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
    height: ${ROTATION_HEIGHT[size]}px;

	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
    min-height: ${ROTATION_HEIGHT[size]}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
    max-height: ${ROTATION_HEIGHT[size]}px;
  `,
);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Inner = styled.div<{
	isTrueStartTime: boolean;
	isTrueEndTime: boolean;
	color: string;
	hasBorder: boolean;
	hasDashedBorder: boolean;
	layout: Layout;
	size: 'small' | 'medium' | 'large';
}>`
	position: relative;
	box-sizing: border-box;

	:focus-visible {
		border: ${SHIFT_BORDER}px solid transparent;
		z-index: 2;
	}

	overflow: hidden;

	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	width: 100%;
	height: 100%;

	cursor: pointer;
	color: ${({ color }) => color};

	${({ hasDashedBorder }) =>
		hasDashedBorder &&
		`
      border: ${SHIFT_BORDER}px dashed ${borderColor.noone};
    `}

	${({ hasBorder }) =>
		hasBorder &&
		`
      box-shadow: ${token('elevation.shadow.raised', `1px 2px 3px ${N60A}`)};
    `};

	${({ hasBorder }) =>
		hasBorder &&
		`
      border: ${SHIFT_BORDER}px solid ${borderColor.default};
    `};

	${({ isTrueStartTime, isTrueEndTime }) =>
		computeBorderRadius({
			leftRounded: isTrueStartTime,
			rightRounded: isTrueEndTime,
			radius: `${SHIFT_RADIUS}px`,
		})};

	${({ layout, size, hasDashedBorder, hasBorder }) => {
		const padding = CONTENT_PADDING[size];

		const offset = hasBorder || hasDashedBorder ? SHIFT_BORDER : 0;

		switch (layout) {
			case Layout.FullView:
				return `
          padding: ${padding - offset}px;

          :focus-visible {
            padding: ${padding - SHIFT_BORDER}px;
          }

          justify-content: space-between;
        `;
			case Layout.CompactView:
			case Layout.MiniView:
			case Layout.SparseView:
				return `
          padding: ${padding - offset}px;

          :focus-visible {
            padding: ${padding - SHIFT_BORDER}px;
          }

          justify-content: flex-start;
        `;
			case Layout.NoneView:
				return `
          padding: 0;
        `;

			case Layout.FullSlimView:
			case Layout.SparseSlimView:
				return `
          padding: 0 ${padding - offset}px;

          :focus-visible {
            padding: ${padding - SHIFT_BORDER}px;
          }

          justify-content: center;
        `;
			case Layout.CompactSlimView:
			case Layout.MiniSlimView:
			case Layout.NoneSlimView:
				return `
          padding: 0 ;

          justify-content: center;
        `;
		}
	}};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const OverrideBorder = styled.div<{
	mini: boolean;
}>`
	position: absolute;
	left: 0;
	bottom: 0;
	z-index: 0;

	height: ${({ mini }) => OVERRIDE_BORDER - (mini ? SHIFT_BORDER / 2 : 0)}px;
	width: 100%;

	background-image: repeating-linear-gradient(
		-45deg,
		${OVERRIDE_IMAGE.BACKGROUND},
		${OVERRIDE_IMAGE.BACKGROUND} ${token('space.100', '8px')},
		${OVERRIDE_IMAGE.FOREGROUND} ${token('space.100', '8px')},
		${OVERRIDE_IMAGE.FOREGROUND} ${token('space.200', '16px')}
	);
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Gradient = styled.div({
	position: 'absolute',
	left: 0,
	bottom: 0,
	zIndex: 0,
	height: '100%',
	width: '100%',
	backgroundColor: token('elevation.surface', N0),
	backgroundImage: `repeating-linear-gradient( -45deg, ${
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		DELETED_IMAGE.BACKGROUND
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	}, ${DELETED_IMAGE.BACKGROUND} ${token('space.050', '4px')}, ${
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		DELETED_IMAGE.FOREGROUND
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	} ${token('space.050', '4px')}, ${DELETED_IMAGE.FOREGROUND} ${token('space.075', '6px')} )`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Background = styled.div<{
	color: string;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>((props) => ({
	position: 'absolute',
	left: 0,
	bottom: 0,
	zIndex: 0,
	height: '100%',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: props.color,
}));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Overlay = styled.div<{
	nowFillRatio: number;
	nowLocation: NowLocation;
	isTrueStartTime: boolean;
	isTrueEndTime: boolean;
}>`
	background-color: ${token('color.background.accent.gray.subtlest', N20)};
	content: '';
	height: 100%;
	left: 0;
	opacity: 0.5;
	position: absolute;
	top: 0;
	z-index: 2;

	${({ nowLocation, nowFillRatio }) => {
		switch (nowLocation) {
			case NowLocation.InRange:
				return `width: ${nowFillRatio * 100}%;`;
			case NowLocation.After:
				return 'width: 100%;';
			case NowLocation.Before:
				return 'display: none;';
		}
	}};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const User = styled.div<{ layout: Layout }>`
	align-items: center;
	display: flex;
	justify-content: center;
	width: 100%;
	z-index: 2;

	${({ layout }) => {
		switch (layout) {
			case Layout.CompactView:
			case Layout.MiniView:
				return `
          flex-direction: column;
          height: 100%;
          justify-content: space-between;
        `;

			case Layout.NoneView:
			case Layout.NoneSlimView:
				return `
          display: none;
        `;

			default:
				return `
          flex-direction: row;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconCommon = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${PROFILE_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${PROFILE_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${PROFILE_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${PROFILE_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${PROFILE_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${PROFILE_SIZE}px`,
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Profile = styled(IconCommon)<{ layout: Layout; hasSvg: boolean }>`
	${({ hasSvg }) =>
		hasSvg &&
		`
    border-radius: 50%;
    overflow: hidden;

    background-color: ${iconColor.backgroundColor};
    color: ${iconColor.color};

    & svg {
      background-color: ${iconColor.backgroundColor};
      color: ${iconColor.color};
    }
    `}

	${({ layout }) => {
		switch (layout) {
			case Layout.CompactSlimView:
			case Layout.MiniSlimView:
			case Layout.NoneSlimView:
			case Layout.NoneView:
				return `
          display: none;
        `;

			case Layout.FullView:
			case Layout.FullSlimView:
			case Layout.SparseView:
			case Layout.SparseSlimView:
				return `
          margin-right: ${ICON_MARGIN}px;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MissingResponder = styled(IconCommon)<{ layout: Layout }>`
	color: ${token('color.icon.danger', R300)};

	${({ layout }) => {
		switch (layout) {
			case Layout.NoneSlimView:
			case Layout.NoneView:
				return `
          display: none;
        `;

			case Layout.FullView:
			case Layout.FullSlimView:
			case Layout.SparseView:
			case Layout.SparseSlimView:
				return `
          margin-right: ${ICON_MARGIN}px;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Name = styled.span<{ layout: Layout }>`
	color: inherit;
	margin: 0;
	overflow: hidden;
	text-align: center;
	text-overflow: ellipsis;
	white-space: nowrap;

	line-height: ${PROFILE_SIZE}px;

	${({ layout }) => {
		switch (layout) {
			case Layout.CompactView:
			case Layout.MiniView:
				return `
          line-height: ${TIME_LINE_HEIGHT}px;
        `;
		}
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Time = styled.time<{ layout: Layout }>`
	color: inherit;
	display: none;
	font-size: ${TIME_FONT_SIZE}px;
	line-height: ${TIME_LINE_HEIGHT}px;
	overflow: hidden;
	white-space: nowrap;
	z-index: 2;

	${({ layout }) => {
		switch (layout) {
			case Layout.FullView:
				return `
          display: block;
        `;
		}
	}}
`;
